import React, { useState, useEffect, useRef, useCallback } from "react";
import { TextField, Box, Typography, Stack, InputAdornment } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getIntlTranslation } from "../../../../utils/helper";

const ColoredTextField = styled(TextField)(({ bordercolor }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    fontWeight: 600,
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: bordercolor,
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: bordercolor,
      borderWidth: '2px',
    },
    '&.Mui-focused fieldset': {
      borderColor: bordercolor,
      borderWidth: '2.5px',
    },
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    fontWeight: 600,
    color: bordercolor,
    padding: '10px 12px 10px 6px',
    fontSize: '0.875rem',
  },
  '& .MuiInputAdornment-root': {
    marginLeft: '2px',
    fontSize: '0.75rem',
  },
}));

const FIELDS = ['short', 'medium', 'long'];

const FIELD_CONFIG = {
  short: { label: '_Short-Term', color: '#e74c3c' },
  medium: { label: '_Medium-Term', color: '#f39c12' },
  long: { label: '_Long-Term', color: '#27ae60' },
};

const ModernPriorityRangeSlider = ({ onChange, data }) => {
  const [values, setValues] = useState({ short: '30', medium: '40', long: '30' });
  
  const isUserEditingRef = useRef(false);
  const editHistoryRef = useRef([]);
  const latestValuesRef = useRef({ short: '30', medium: '40', long: '30' }); // Track latest values

  // Sync with parent data
  useEffect(() => {
    if (isUserEditingRef.current || !data?.length) return;
    
    const short = Number(data[0]);
    const medium = Number(data[1] - data[0]);
    const long = Number(100 - data[1]);
    
    const newValues = {
      short: String(short),
      medium: String(medium),
      long: String(long),
    };
    
    setValues(newValues);
    latestValuesRef.current = newValues;
    editHistoryRef.current = [];
  }, [data]);

  const notifyChange = useCallback((vals) => {
    if (typeof onChange !== "function") return;
    const short = Number(vals.short) || 0;
    const medium = Number(vals.medium) || 0;
    onChange([short, short + medium]);
  }, [onChange]);

  const updateEditHistory = useCallback((field) => {
    const history = editHistoryRef.current.filter(f => f !== field);
    history.push(field);
    editHistoryRef.current = history.slice(-2);
  }, []);

  const handleChange = useCallback((field, value) => {
    isUserEditingRef.current = true;
    
    const sanitized = value.replace(/[^\d.]/g, '');
    if ((sanitized.match(/\./g) || []).length > 1) return;
    
    updateEditHistory(field);
    
    const newValues = { ...latestValuesRef.current, [field]: sanitized };
    
    // Auto-fill: if 2 fields edited, calculate the 3rd
    if (editHistoryRef.current.length === 2) {
      const autoFillField = FIELDS.find(f => !editHistoryRef.current.includes(f));
      const sourceFields = FIELDS.filter(f => f !== autoFillField);
      
      const allValid = sourceFields.every(f => {
        const v = newValues[f];
        return v !== '' && v !== '.' && !isNaN(parseFloat(v));
      });
      
      if (allValid && autoFillField) {
        const sum = sourceFields.reduce((acc, f) => acc + (parseFloat(newValues[f]) || 0), 0);
        const calculated = Math.round(Math.max(0, 100 - sum) * 100) / 100;
        newValues[autoFillField] = String(calculated);
      }
    }
    
    latestValuesRef.current = newValues; // Always keep ref in sync
    setValues(newValues);
  }, [updateEditHistory]);

  const handleBlur = useCallback(() => {
    // Use ref to get the absolute latest values
    const currentValues = latestValuesRef.current;
    
    const cleaned = {};
    FIELDS.forEach(f => {
      const num = parseFloat(currentValues[f]) || 0;
      cleaned[f] = String(Math.round(Math.max(0, num) * 100) / 100);
    });
    
    latestValuesRef.current = cleaned;
    setValues(cleaned);
    notifyChange(cleaned);
    
    setTimeout(() => { isUserEditingRef.current = false; }, 100);
  }, [notifyChange]);

  const handleFocus = useCallback(() => {
    isUserEditingRef.current = true;
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
  }, []);

  // Validation
  const total = FIELDS.reduce((sum, f) => sum + (parseFloat(values[f]) || 0), 0);
  const displayTotal = Math.round(total * 100) / 100;
  const isValid = Math.abs(total - 100) < 0.01;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={1.5} sx={{ mb: 0 }}>
        {FIELDS.map((field) => (
          <Box key={field} sx={{ flex: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                textAlign: 'center',
                color: FIELD_CONFIG[field].color, 
                fontWeight: 600, 
                fontSize: '0.8rem',
                mb: 0.5
              }}
            >
              {getIntlTranslation(FIELD_CONFIG[field].label)}
            </Typography>
            <ColoredTextField
              fullWidth
              type="text"
              value={values[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyPress={handleKeyPress}
              bordercolor={FIELD_CONFIG[field].color}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              size="small"
              placeholder="0"
            />
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 1, textAlign: 'center' }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: isValid ? '#27ae60' : '#e74c3c',
            fontWeight: 600,
            fontSize: '0.75rem'
          }}
        >
          Gesamt: {displayTotal}% {isValid ? '✓' : '(must equal 100%)'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ModernPriorityRangeSlider;


//This comment needs TO BE REMOVED 